import React, { useCallback, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import toast from "react-hot-toast";

import Drawer from "@mui/material/Drawer";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import Checkbox from "@mui/material/Checkbox";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import MuiFormControlLabel from "@mui/material/FormControlLabel";

import FormButtons from "views/components/formbuttons";
import FormHeader from "views/components/formheader";
import APICall from "network/APICall";
import EndPoints from "network/Endpoints";
import { isSpaceValidateTextField } from "@core/utils/Const";

const FormControlLabel = styled(MuiFormControlLabel)(({ theme }) => ({
  "& .MuiFormControlLabel-label": {
    fontSize: "0.875rem",
    color: theme.palette.text.secondary,
  },
}));

const UpdateLiveProgram = (props) => {
  const {
    open,
    toggle,
    heading,
    updateData,
    generalData,
    handleUpdate,
    programList,
  } = props;

  const defaultValues = {
    program: "",
    title: "",
    notification: "",
  };

  const {
    setValue,
    reset,
    control,
    clearErrors,
    handleSubmit,
    formState: { errors },
  } = useForm({ defaultValues });

  const resetToStoredValues = useCallback(() => {
    const event = {
      program: updateData?.live_program,
      title: updateData?.title,
      notification: updateData?.isNotification,
      programId: updateData?.program_id,
    };
    setValue("program", event.program || "");
    setValue("title", event.title || "");
    setValue("notification", event.notification || false);
    setValue("programId", parseInt(event.programId) || "");
  }, [
    setValue,
    updateData?.isNotification,
    updateData?.live_program,
    updateData?.program_id,
    updateData?.title,
  ]);

  useEffect(() => {
    resetToStoredValues();
  }, [open, resetToStoredValues]);

  const handleClose = () => {
    clearErrors();
    reset();
    toggle();
  };

  const onSubmit = (data) => {
    const payload = {
      liveProgram: data.program,
      status: generalData?.is_live_program,
      title: data.title,
      isNotification: data.notification,
      programId: data.programId.toString(),
    };
    APICall("post", payload, EndPoints.updateLiveProgram)
      .then((response) => {
        const { isError, message } = response.data;
        if (isError) {
          toast.error(message);
          return;
        }
        toast.success(message);
        handleUpdate();
        handleClose();
      })
      .catch((error) => {
        return error;
      });
  };

  return (
    <Drawer
      open={open}
      anchor="right"
      variant="temporary"
      onClose={handleClose}
      ModalProps={{ keepMounted: true }}
      sx={{ "& .MuiDrawer-paper": { width: { xs: 300, sm: 400 } } }}
    >
      <FormHeader heading={heading} onClick={handleClose} />
      <Box sx={{ p: 5 }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <Controller
                  name="title"
                  control={control}
                  rules={{
                    required: true,
                    validate: isSpaceValidateTextField,
                  }}
                  render={({ field: { value, onChange } }) => (
                    <TextField
                      value={value}
                      onChange={onChange}
                      fullWidth
                      type="text"
                      label="Title"
                      placeholder="Enter title"
                      error={Boolean(errors.title)}
                      aria-describedby="validation-basic-first-name"
                    />
                  )}
                />
                {errors.title && (
                  <FormHelperText
                    sx={{ color: "error.main" }}
                    id="validation-basic-first-name"
                  >
                    This field is required
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <Controller
                  name="program"
                  control={control}
                  rules={{
                    required: true,
                    validate: isSpaceValidateTextField,
                  }}
                  render={({ field: { value, onChange } }) => (
                    <TextField
                      value={value}
                      onChange={onChange}
                      fullWidth
                      type="text"
                      label="Program"
                      placeholder="Enter program URL"
                      error={Boolean(errors.program)}
                      aria-describedby="validation-basic-first-name"
                    />
                  )}
                />
                {errors.program && (
                  <FormHelperText
                    sx={{ color: "error.main" }}
                    id="validation-basic-first-name"
                  >
                    This field is required
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <Controller
                  name="programId"
                  control={control}
                  rules={{ required: false }}
                  render={({ field: { value, onChange } }) => (
                    <>
                      <InputLabel
                        id="validation-basic-select"
                        error={Boolean(errors.hijriDate)}
                        htmlFor="validation-basic-select"
                      >
                        Link Program
                      </InputLabel>
                      <Select
                        label={"Link Program"}
                        onChange={onChange}
                        labelId="validation-basic-select"
                        aria-describedby="validation-basic-select"
                        value={value}
                      >
                        <MenuItem value="">None</MenuItem>
                        {programList.map((item, index) => (
                          <MenuItem value={item.id} key={index}>
                            {item.title}
                          </MenuItem>
                        ))}
                      </Select>
                    </>
                  )}
                />
              </FormControl>
            </Grid>

            <Grid xs={12} item>
              <FormControl fullWidth>
                <Controller
                  name="notification"
                  control={control}
                  rules={{ required: false }}
                  render={({ field: { value, onChange } }) => {
                    return (
                      <FormControlLabel
                        control={
                          <Checkbox checked={value} onChange={onChange} />
                        }
                        label={
                          <Typography variant="body1" component="span">
                            Send Notification
                          </Typography>
                        }
                      />
                    );
                  }}
                />
              </FormControl>
            </Grid>

            <FormButtons type={"edit"} />
          </Grid>
        </form>
      </Box>
    </Drawer>
  );
};

export default UpdateLiveProgram;
